<template>
  <v-container fluid>
    <v-row dense>
      <v-col v-for="(field, index) in timeFields" :key="index" cols="auto">
        <div class="d-flex flex-column">
          <div class="text-bold mb-1 font-weight-bold">{{ field.label }}</div>
          <v-row no-gutters class="mr-3 pr-5" style="width: 200px">
            <v-col cols="7">
              <v-select
                v-model="field.unit"
                :disabled="fieldDisabled || field.disabled"
                :items="timeUnits"
              />
            </v-col>
            <v-col cols="5">
              <v-text-field
                v-model="field.value"
                :disabled="fieldDisabled || field.disabled"
                type="number"
              />
            </v-col>
          </v-row>
        </div>
      </v-col>

      <v-col
        v-for="(checkbox, index) in checkboxFields"
        :key="`checkbox-${index}`"
        cols="auto"
      >
        <div class="d-flex flex-column align-center" style="min-width: 120px">
          <div class="text-bold mb-1 font-weight-bold">
            {{ checkbox.label }}
          </div>
          <v-checkbox
            v-model="checkbox.value"
            :disabled="fieldDisabled"
            density="compact"
            hide-details
          />
        </div>
      </v-col>
    </v-row>

    <v-row class="mb-2">
      <v-col
        v-for="(header, index) in tableHeaders"
        :key="index"
        :cols="header.cols"
      >
        <div class="text-subtitle-2 font-weight-bold">{{ header.title }}</div>
      </v-col>
    </v-row>

    <v-row v-for="row in tableRows" :key="row.type" align="center">
      <v-col cols="2">{{ incidentTypeLabel(row.type) }}</v-col>
      <v-col cols="2">
        <v-select
          v-model="row.taskAssignmentRole"
          :items="roleOptions"
          density="compact"
          variant="outlined"
          hide-details
          class="mb-2"
          bg-color="white"
          :disabled="fieldDisabled"
        />
      </v-col>
      <v-col cols="3">
        <v-select
          v-model="row.assigneeForApprovalRole"
          :items="roleOptions"
          density="compact"
          variant="outlined"
          hide-details
          class="mb-2"
          bg-color="white"
          :disabled="fieldDisabled"
        />
      </v-col>
      <v-col cols="2">
        <v-btn
          variant="outlined"
          size="small"
          @click="openNotificationModal(row)"
        >
          View Default
        </v-btn>
      </v-col>
      <v-col cols="3">
        <v-btn
          variant="outlined"
          size="small"
          @click="viewAssignedIncidents(row.type)"
          >View</v-btn
        >
      </v-col>
    </v-row>

    <v-row>
      <v-col class="d-flex justify-center">
        <v-btn :disabled="fieldDisabled" color="success" @click="handleSave"
          >Save</v-btn
        >
      </v-col>
    </v-row>

    <DialogLayout
      v-model="isOpenNotificationModal"
      title="Notification Settings"
      width="900px"
    >
      <NotificationModal
        :read-only="fieldDisabled"
        :settings="notificationLocalSettings"
      />
      <template #actions>
        <div class="d-flex justify-center">
          <v-btn
            :disabled="fieldDisabled"
            color="primary"
            class=" "
            :loading="saving"
            @click="saveNotificationSettings"
          >
            Save Changes
          </v-btn>
        </div>
      </template>
    </DialogLayout>
  </v-container>
</template>

<script>
import { UPDATE_ORGANIZATION_FEATURE } from "./graphql";
import { ref } from "vue";
import { titleCase } from "@tod-ui/helpers/strings";
import { incidentDisplayType } from "../common/helpers/incident";
import DialogLayout from "../../components/layout/DialogLayout.vue";
import NotificationModal from "./NotificationModal.vue";
import useFacility from "../authentication/useFacility";
import useUser from "../authentication/useUser";
import {
  secondsToUnit,
  convertToSeconds,
  PRIORITY_INCIDENT_TYPES,
  defaultManagerNotificationSettings,
  findSettingByType,
  normalizeIncidentType,
} from "./helper";
export default {
  name: "CardPriorityContent",
  components: {
    DialogLayout,
    NotificationModal,
  },
  props: {
    name: {
      type: String,
      required: true,
    },
    level: {
      type: Number,
      required: true,
    },
    settings: {
      type: Object,
      required: true,
    },
    allPriorityData: {
      type: Array,
      required: true,
    },
    featureId: {
      type: String,
      default: null,
    },
    fieldDisabled: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const isOpenNotificationModal = ref(false);
    const selectedNotificationSettings = ref(null);
    const selectedIncidentType = ref(null);
    const notificationLocalSettings = ref([]);
    const { currentFacilityId, facilityFeatures } = useFacility();
    const { defaultOrganization } = useUser();

    return {
      defaultOrganization,
      currentFacilityId,
      isOpenNotificationModal,
      selectedNotificationSettings,
      selectedIncidentType,
      notificationLocalSettings,
      facilityFeatures,
    };
  },
  data() {
    return {
      localTableRows: [],
      timeUnits: ["Hours", "Days", "Weeks", "Months"],
      saving: false,
      localSettings: {},
      showNotificationModal: false,
      roleOptions: [
        { text: "Facility Manager", value: "facility_manager" },
        { text: "Regional Manager", value: "regional_manager" },
        { text: "Organization Manager", value: "organization_manager" },
      ],
    };
  },

  computed: {
    timeFields() {
      const commonSettings = this.settings?.commonSettings || {};
      const openIn = this.secondsToUnit(commonSettings.incidentOpenIn);
      const progressIn = this.secondsToUnit(commonSettings.incidentProgressIn);
      const reviewedIn = this.secondsToUnit(commonSettings.incidentReviewedIn);
      const totalSeconds =
        (commonSettings.incidentOpenIn || 0) +
        (commonSettings.incidentProgressIn || 0) +
        (commonSettings.incidentReviewedIn || 0);

      const resolvedIn = this.secondsToUnit(totalSeconds);

      return [
        {
          label: "Incident Open In",
          value: openIn.value,
          unit: openIn.unit,
        },
        {
          label: "Incident Progress In",
          value: progressIn.value,
          unit: progressIn.unit,
        },
        {
          label: "Incident Reviewed In",
          value: reviewedIn.value,
          unit: reviewedIn.unit,
        },
        {
          label: "Incident Resolved In",
          value: resolvedIn.value,
          unit: resolvedIn.unit,
          disabled: true,
        },
      ];
    },
    checkboxFields() {
      const commonSettings = this.settings?.commonSettings || {};
      return [
        {
          label: "Create Auto Task",
          value: commonSettings.createAutoTask || false,
        },
        {
          label: "Inspection Required",
          value: commonSettings.inspectionRequired || false,
        },
        {
          label: "Approval Required",
          value: commonSettings.approvalRequired || false,
        },
      ];
    },
    tableHeaders() {
      return [
        { title: "Incident Category", cols: 2 },
        { title: "Task Assignment Role", cols: 2 },
        { title: "Assignee for Approval Role", cols: 3 },
        { title: "Notification Preference", cols: 2 },
        { title: "View Assigned Incidents", cols: 3 },
      ];
    },
    tableRows: {
      get() {
        if (this.localTableRows.length) {
          return this.localTableRows;
        }

        return this.buildTableRows(this.settings?.incidentSettings);
      },
      set(newValue) {
        this.localTableRows = newValue;
      },
    },
  },
  watch: {
    timeFields: {
      deep: true,
      handler(newFields) {
        const changedField = newFields.find(
          (field) =>
            field.label !== "Incident Resolved In" &&
            field.value !==
              this.timeFields.find((f) => f.label === field.label)?.value
        );

        if (changedField) {
          const totalSeconds = newFields
            .filter((field) => field.label !== "Incident Resolved In")
            .reduce((sum, field) => {
              return sum + this.convertToSeconds(field.value, field.unit);
            }, 0);
        }
      },
    },
    "settings.incidentSettings": {
      immediate: true,
      handler(newSettings) {
        if (!newSettings) return;

        this.localTableRows = this.buildTableRows(newSettings);
      },
    },
    isOpenNotificationModal(newValue) {
      if (!newValue) {
        this.closeNotificationModal();
      }
    },
  },

  methods: {
    incidentTypeLabel(type) {
      return titleCase(incidentDisplayType(type));
    },

    getDefaultNotificationSettings() {
      return defaultManagerNotificationSettings();
    },

    // One row per type in PRIORITY_INCIDENT_TYPES, hydrated from the stored
    // setting with the same `type`. Matching by type (not array index) is what
    // keeps humidity bound to humidity — the stored array also holds `water`,
    // which used to shift every row after it.
    buildTableRows(incidentSettings) {
      return PRIORITY_INCIDENT_TYPES.map((type) => {
        const setting = findSettingByType(incidentSettings, type);

        return {
          type,
          taskAssignmentRole: setting?.taskAssignmentRole || "facility_manager",
          assigneeForApprovalRole:
            setting?.assigneeForApprovalRole || "regional_manager",
          notificationSettings: setting?.notificationSettings
            ?.managerNotificationSettings?.length
            ? this.cleanTypename(
                setting.notificationSettings.managerNotificationSettings
              )
            : this.getDefaultNotificationSettings(),
        };
      });
    },

    // Merge the edited rows into the stored settings. Types the UI does not
    // show (water, battery) pass through untouched, and a row with no stored
    // setting yet is appended instead of being dropped from the payload.
    mergeIncidentSettings(storedSettings) {
      const stored = storedSettings || [];

      const merged = stored.map((setting) => {
        const matchingRow = this.tableRows.find(
          (row) => row.type === normalizeIncidentType(setting?.type)
        );

        if (!matchingRow) return setting;

        return {
          ...setting,
          type: setting.type,
          taskAssignmentRole:
            matchingRow.taskAssignmentRole || setting.taskAssignmentRole,
          assigneeForApprovalRole:
            matchingRow.assigneeForApprovalRole ||
            setting.assigneeForApprovalRole,
          notificationSettings: {
            managerNotificationSettings:
              matchingRow.notificationSettings ||
              setting.notificationSettings?.managerNotificationSettings,
          },
        };
      });

      const missingRows = this.tableRows.filter(
        (row) =>
          !stored.some(
            (setting) => normalizeIncidentType(setting?.type) === row.type
          )
      );

      return merged.concat(
        missingRows.map((row) => ({
          type: row.type,
          taskAssignmentRole: row.taskAssignmentRole,
          assigneeForApprovalRole: row.assigneeForApprovalRole,
          notificationSettings: {
            managerNotificationSettings: row.notificationSettings,
          },
        }))
      );
    },

    cleanTypename(data) {
      if (Array.isArray(data)) {
        return data.map((item) => this.cleanTypename(item));
      }

      if (data && typeof data === "object") {
        const cleaned = {};
        for (const [key, value] of Object.entries(data)) {
          if (key !== "__typename") {
            cleaned[key] = this.cleanTypename(value);
          }
        }
        return cleaned;
      }

      return data;
    },

    openNotificationModal(row) {
      this.selectedIncidentType = normalizeIncidentType(row.type);
      const cleanSettings = row.notificationSettings?.length
        ? this.cleanTypename(row.notificationSettings)
        : this.getDefaultNotificationSettings();

      this.notificationLocalSettings = cleanSettings;
      this.isOpenNotificationModal = true;
    },

    closeNotificationModal() {
      this.isOpenNotificationModal = false;
      this.notificationLocalSettings = [];
      this.selectedIncidentType = null;
    },
    viewAssignedIncidents(incidentType) {
      this.$emit(
        "view-assigned-incidents",
        normalizeIncidentType(incidentType),
        this.name
      );
    },
    async saveNotificationSettings() {
      try {
        this.saving = true;
        this.localTableRows = this.tableRows.map((row) => {
          if (normalizeIncidentType(row.type) === this.selectedIncidentType) {
            return {
              ...row,
              notificationSettings: this.notificationLocalSettings,
            };
          }
          return row;
        });

        await this.handleSave();
        this.closeNotificationModal();
      } catch (error) {
        console.error("Error saving notification settings:", error);
      } finally {
        this.saving = false;
      }
    },

    async handleSave() {
      try {
        this.saving = true;

        const updatedPriority = this.allPriorityData.map((item) => {
          if (item.name === this.name) {
            const openInField = this.timeFields.find(
              (f) => f.label === "Incident Open In"
            );
            const progressInField = this.timeFields.find(
              (f) => f.label === "Incident Progress In"
            );
            const reviewedInField = this.timeFields.find(
              (f) => f.label === "Incident Reviewed In"
            );
            const resolvedInField = this.timeFields.find(
              (f) => f.label === "Incident Resolved In"
            );

            const updatedIncidentSettings = this.mergeIncidentSettings(
              item.incidentSettings
            );

            return {
              name: this.name,
              level: this.level,
              commonSettings: {
                createAutoTask: this.checkboxFields.find(
                  (f) => f.label === "Create Auto Task"
                )?.value,
                approvalRequired: this.checkboxFields.find(
                  (f) => f.label === "Approval Required"
                )?.value,
                inspectionRequired: this.checkboxFields.find(
                  (f) => f.label === "Inspection Required"
                )?.value,
                incidentOpenIn: this.convertToSeconds(
                  openInField?.value,
                  openInField?.unit
                ),
                incidentProgressIn: this.convertToSeconds(
                  progressInField?.value,
                  progressInField?.unit
                ),
                incidentResolvedIn: this.convertToSeconds(
                  resolvedInField?.value,
                  resolvedInField?.unit
                ),
                incidentReviewedIn: this.convertToSeconds(
                  reviewedInField?.value,
                  reviewedInField?.unit
                ),
              },
              incidentSettings: updatedIncidentSettings,
            };
          }
          return item;
        });

        const cleanedPriority = this.cleanTypename(updatedPriority);

        const result = await this.$apollo.mutate({
          mutation: UPDATE_ORGANIZATION_FEATURE,
          variables: {
            id: this.featureId,
            organizationId: this.defaultOrganization.id,
            config: {
              incidentPriorityMatrix: {
                incidentPriorityMatrix: cleanedPriority,
              },
            },
          },
        });

        this.$emit("save-success", result);
      } catch (error) {
        console.error("Save error:", error);
        this.$emit("save-error", error);
      } finally {
        this.saving = false;
      }
    },
    secondsToUnit,
    convertToSeconds,
  },
};
</script>
